import { useState, useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { newReplyId, latestNotify, manualEmailLabel, emailStatusBadge } from "@/lib/cxReply";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import ToggleGroup from "@/components/ToggleGroup";
import UserInlineDetail from "@/components/user/UserInlineDetail";
import Panel from "./Panel";
import Skeleton from "./Skeleton";
import MobileCxList from "@/components/MobileCxList";
import { useIsMobile } from "@/hooks/useMediaQuery";

// "고객 문의" 탭 (2026-07-19) — 자체 상담 시스템 운영 콘솔 (채널톡 대체 결정).
// 접수 흐름: 앱 Help→문의하기 → nova-support-prod → 슬랙 즉시 알림 → 여기서 응대.
// 소스 2종: 문의(티켓 — 상태·답변 관리) / 의견(VOC — 읽기 전용 피드백).
//
// v3 3단 인박스(2026-07-24): 표+행 확장 방식을 헬프데스크형 3단 레이아웃으로 재설계.
// 좌(문의 목록·상태 필터) / 중앙(대화 스레드·답장) / 우(고객 상세·통계·빠른 작업).
// 목록은 state=all 로 한 번 받아 클라이언트에서 필터 — 탭 전환이 재조회 없이 즉시.

// ToggleGroup 은 { id, label } 계약 — value 키로 넣으면 클릭해도 onChange(undefined)가
// 호출돼 토글이 전환되지 않는다(2026-07-21 버그 수정).
const SOURCE_OPTIONS = [
  { id: "tickets", label: "문의 · 오류" },
  { id: "feedback", label: "의견 · 제안" },
  { id: "voc", label: "VOC" },
];
const LIST_FILTERS = [
  { id: "all", label: "전체" },
  { id: "open", label: "열림" },
  { id: "answered", label: "답변완료" },
  { id: "closed", label: "닫힘" },
];
const STATE_BADGE = {
  open: { label: "열림", cls: "bg-red-50 text-red-500" },
  answered: { label: "답변완료", cls: "bg-emerald-50 text-emerald-600" },
  closed: { label: "닫힘", cls: "bg-ink-100 text-ink-500" },
};

// CX Phase 1 (2026-08-25) — 내부 운영 축. canonical value 는 영어 enum, UI 는 한국어.
// severity = 문제 심각도, processingStatus = 내부 처리 진행(대화 상태 status 와 별개 축).
const SEVERITY_META = {
  critical: { label: "긴급", cls: "bg-red-100 text-red-700" },
  high:     { label: "높음", cls: "bg-orange-100 text-orange-700" },
  normal:   { label: "보통", cls: "bg-ink-100 text-ink-600" },
  low:      { label: "낮음", cls: "bg-sky-100 text-sky-700" },
};
const SEVERITY_OPTIONS = [
  { id: "critical", label: "긴급" }, { id: "high", label: "높음" },
  { id: "normal", label: "보통" }, { id: "low", label: "낮음" },
];
const PROC_OPTIONS = [
  { id: "new", label: "신규" }, { id: "assigned", label: "담당자 지정" },
  { id: "in_progress", label: "처리 중" }, { id: "resolved", label: "해결" },
];

function SeverityBadge({ severity }) {
  const m = SEVERITY_META[severity] || SEVERITY_META.normal;
  return <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.label}</span>;
}

// 로컬 디자인 프리뷰(2026-07-24) — 백엔드/로그인 없이 3단 레이아웃을 확인하기 위한
// 샘플 티켓. import.meta.env.DEV + VITE_DEV_NO_AUTH=1 일 때만 API 대신 이 목데이터를
// 쓰고, 답장·상태 변경은 로컬 state 로만 처리한다(프로덕션 빌드에선 DEV=false 라 꺼짐).
const DEV_PREVIEW = import.meta.env.DEV && import.meta.env.VITE_DEV_NO_AUTH === "1";

function _agoISO(hours) {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}
function buildMockTickets() {
  return [
    {
      id: "t1", email: "kim.minjae@gmail.com", userId: "usr_8f3a2b", subject: "결제 오류",
      status: "open", createdAt: _agoISO(16), updatedAt: _agoISO(11),
      messages: [
        { from: "user", text: "결제했는데 크레딧이 안 들어왔어요. 확인 부탁드립니다.", at: _agoISO(16) },
        { from: "admin", text: "안녕하세요 김민재님, 확인해보겠습니다. 결제하신 정확한 시간과 토스 결제 승인번호를 알려주시겠어요?", at: _agoISO(16), by: "ai@sedaily.com" },
        { from: "user", text: "승인번호는 TPS-2026072209-00412 이고 오전 9시 14분쯤 결제했어요.", at: _agoISO(16) },
        { from: "admin", text: "확인했습니다. 결제 처리 중 일시적 지연이 있었네요. 지금 수동으로 크레딧 150개를 충전해드렸습니다. 확인 부탁드립니다!", at: _agoISO(16), by: "ai@sedaily.com" },
        { from: "user", text: "확인했습니다 들어와있네요. 감사합니다 😊", at: _agoISO(12) },
        { from: "user", text: "그런데 혹시 이런 일이 또 생기면 자동으로 복구되나요?", at: _agoISO(11) },
      ],
    },
    {
      id: "t2", email: "park.soyeon@sedaily.com", userId: "usr_1c77de", subject: "기능 문의",
      status: "answered", createdAt: _agoISO(58), updatedAt: _agoISO(55),
      messages: [
        { from: "user", text: "스캔 문서였는데 텍스트 PDF로 다시 변환해서 올려볼게요. 감사합니다.", at: _agoISO(58) },
        { from: "admin", text: "네, 텍스트 레이어가 있는 PDF면 인식률이 훨씬 좋습니다. 다시 시도해 보시고 안 되면 알려주세요!", at: _agoISO(55), by: "ai@sedaily.com" },
      ],
    },
    {
      id: "t3", email: "lee.junghoon@naver.com", userId: "usr_44a0b9", subject: "로그인",
      status: "open", createdAt: _agoISO(14), updatedAt: _agoISO(14),
      messages: [
        { from: "user", text: "비밀번호를 잊어버렸는데 재설정 이메일이 안 옵니다. 스팸함도 확인했는데 없네요.", at: _agoISO(14) },
      ],
    },
    {
      id: "t4", email: "choi.yuna@company.co.kr", userId: "usr_9de210", subject: "일반",
      status: "closed", createdAt: _agoISO(92), updatedAt: _agoISO(90),
      messages: [
        { from: "user", text: "찾았어요! 해결됐습니다. 감사합니다.", at: _agoISO(92) },
        { from: "admin", text: "다행입니다. 이용해 주셔서 감사합니다 :)", at: _agoISO(90), by: "ai@sedaily.com" },
      ],
    },
    {
      id: "t5", email: "hong.gildong@press.kr", userId: "usr_02f8c1", subject: "제휴 제안",
      status: "answered", createdAt: _agoISO(93), updatedAt: _agoISO(88),
      messages: [
        { from: "user", text: "좋은 제안 감사합니다! 저희가 'MyStyle'이라는 문체 분석 기능을 준비 중인데 관련해 논의하고 싶습니다.", at: _agoISO(93) },
        { from: "admin", text: "관심 가져주셔서 감사합니다. 제휴 담당자에게 전달드렸고 곧 회신드리겠습니다.", at: _agoISO(88), by: "ai@sedaily.com" },
      ],
    },
  ];
}

function StatusBadge({ status }) {
  const b = STATE_BADGE[status] || STATE_BADGE.open;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${b.cls}`}>
      {b.label}
    </span>
  );
}

// 목록·말풍선용 상대 시각 — 24h 안이면 "N시간 전", 넘으면 "7. 22. 오전 2:05".
function fmtRelative(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}분 전`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
    return d.toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "-"; }
}

// 상세 패널용 전체 시각 — "2026년 7월 22일 오후 6:15".
function fmtFull(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("ko-KR", {
      year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return "-"; }
}

function ThreadBubble({ m }) {
  const isAdmin = m.from === "admin";
  return (
    <div className={`flex flex-col ${isAdmin ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
          isAdmin ? "bg-brand-600 text-white" : "border border-ink-200 bg-white text-ink-800"
        }`}
      >
        {m.text}
      </div>
      <div className="mt-1 px-1 text-[11px] text-ink-400">
        {isAdmin && m.by ? `${m.by} · ` : ""}{fmtRelative(m.at)}
      </div>
    </div>
  );
}

// ── 좌: 문의 목록 ────────────────────────────────────────────────────────────
function TicketList({ tickets, filter, onFilter, selectedId, onSelect }) {
  return (
    <div className="flex h-full flex-col rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
      <div className="border-b border-ink-100 px-4 pb-3 pt-4">
        <div className="mb-2.5 text-sm font-semibold text-ink-800">문의 목록</div>
        <div className="flex flex-wrap gap-1.5">
          {LIST_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onFilter(f.id)}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                filter === f.id ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-500 hover:bg-ink-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!tickets.length ? (
          <div className="px-4 py-10 text-center text-[13px] text-ink-400">
            {filter === "open" ? "열린 문의가 없습니다 — 오늘도 평화롭습니다." : "문의 내역이 없습니다."}
          </div>
        ) : (
          tickets.map((t) => {
            const msgs = t.messages || [];
            const last = msgs[msgs.length - 1];
            const preview = last ? `${last.from === "admin" ? "↩ " : ""}${last.text}` : t.text;
            const awaiting = t.status === "open" && last?.from === "user";
            const active = t.id === selectedId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t.id)}
                className={`flex w-full flex-col gap-1 border-b border-ink-100 px-4 py-3 text-left transition-colors ${
                  active ? "bg-brand-50" : "hover:bg-ink-50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13px] font-semibold text-ink-800">{t.email || "익명"}</span>
                  <span className="shrink-0 text-[11px] text-ink-400">{fmtRelative(t.updatedAt || t.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={t.status} />
                  {(t.severity && t.severity !== "normal") && <SeverityBadge severity={t.severity} />}
                  {awaiting && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />}
                  <span className="ml-auto truncate text-[10.5px] text-ink-400">
                    {t.assignee ? `👤 ${t.assignee.split("@")[0]}` : "미배정"}
                  </span>
                </div>
                <p className="line-clamp-1 text-[12.5px] text-ink-500">{preview}</p>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── 중앙: 대화 스레드 + 답장 ──────────────────────────────────────────────────
function ConversationPane({ ticket, onSend, sending, error }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const msgs = ticket?.messages || [];

  // 티켓 전환·새 메시지 도착 시 맨 아래로.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ticket?.id, msgs.length]);

  // 티켓을 바꾸면 작성 중이던 답장 초기화.
  useEffect(() => { setDraft(""); }, [ticket?.id]);

  const submit = () => {
    const text = draft.trim();
    if (!text || sending) return;
    onSend(text, () => setDraft(""));
  };

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl bg-white text-[13px] text-ink-400 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
        왼쪽에서 문의를 선택하세요.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
      <div className="flex items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink-800">{ticket.email || "익명"}</div>
          <div className="mt-0.5 text-[11px] text-ink-400">접수 {fmtRelative(ticket.createdAt)}</div>
        </div>
        <StatusBadge status={ticket.status} />
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {msgs.map((m, i) => <ThreadBubble key={i} m={m} />)}
      </div>

      <div className="border-t border-ink-100 px-3 py-3">
        {error && <div className="mb-2 px-1 text-[12px] text-red-500">{error}</div>}
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            rows={1}
            placeholder="메시지를 입력하세요..."
            className="max-h-32 min-h-[38px] flex-1 resize-none rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-800 outline-none placeholder:text-ink-400 focus:border-brand-400"
          />
          <button
            type="button"
            onClick={submit}
            disabled={sending || !draft.trim()}
            className="shrink-0 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? "전송 중…" : "전송"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 우: 고객 상세 ────────────────────────────────────────────────────────────
function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-[12.5px]">
      <span className="shrink-0 text-ink-400">{label}</span>
      <span className="text-right font-medium text-ink-700">{children}</span>
    </div>
  );
}

// 응대 중 크레딧 지급/회수(2026-07-27) — "결제했는데 크레딧이 안 들어왔어요" 류 문의를
// 전체 사용자 탭으로 이동하지 않고 그 자리에서 처리. 안전장치는 TopUsersTable 의
// QuickAdjust 와 동일 기준: 10만 이상 또는 적용 후 잔액 마이너스면 한 번 더 눌러 확인.
const LARGE_AMOUNT_THRESHOLD = 100_000;

function CreditQuickPanel({ userId }) {
  const env = useFilterStore((s) => s.env);
  const readOnly = isReadOnlyEnv(env);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(false);
  const [sign, setSign] = useState(null); // 1(지급) | -1(회수) | null(닫힘)
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [doneMsg, setDoneMsg] = useState(null);

  useEffect(() => {
    setSign(null); setAmount(""); setReason(""); setConfirming(false);
    setError(null); setDoneMsg(null); setBalance(null); setBalanceError(false);
    if (!userId || DEV_PREVIEW) return;
    const ctrl = new AbortController();
    analyticsApi.adminUserDetail(userId, { env }, ctrl.signal)
      .then((res) => setBalance(res?.balance ?? null))
      .catch((err) => { if (err.name !== "AbortError") setBalanceError(true); });
    return () => ctrl.abort();
  }, [userId, env]);

  const mag = Number(amount);
  const validMag = Number.isInteger(mag) && mag > 0;
  const validReason = reason.trim().length >= 3;
  const signed = (sign || 0) * mag;
  const expected = validMag && balance != null ? balance + signed : null;
  const risky = validMag && (mag >= LARGE_AMOUNT_THRESHOLD || (expected != null && expected < 0));

  const openForm = (s) => {
    setSign(s); setAmount(""); setReason(""); setConfirming(false); setError(null); setDoneMsg(null);
  };

  const submit = async () => {
    if (readOnly) { setError("STG 조회 상태에서는 크레딧 조정이 비활성화됩니다."); return; }
    if (!validMag) { setError("1 이상의 정수를 입력하세요."); return; }
    if (!validReason) { setError("사유를 3자 이상 입력하세요."); return; }
    if (risky && !confirming) { setConfirming(true); setError(null); return; }
    setSubmitting(true);
    setError(null);
    try {
      await analyticsApi.adminAdjustCredit(userId, { amount: signed, reason: reason.trim() });
      setBalance((b) => (b == null ? b : b + signed));
      setDoneMsg(`${signed > 0 ? "+" : ""}${fmt.nc(signed)} 크레딧 ${signed > 0 ? "지급" : "회수"} 완료`);
      setSign(null);
    } catch (err) {
      setError(err?.message || "처리에 실패했습니다 — 잠시 후 다시 시도해 주세요.");
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-b border-ink-100 py-4">
      <div className="mb-1.5 text-xs font-semibold text-ink-500">크레딧</div>
      <InfoRow label="현재 잔액">
        {DEV_PREVIEW ? "— (프리뷰)" : balanceError ? "조회 실패" : balance == null ? "…" : `${fmt.nc(balance)} 크레딧`}
      </InfoRow>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={DEV_PREVIEW || readOnly}
          onClick={() => openForm(1)}
          className={`flex-1 rounded-lg py-2 text-[12.5px] font-semibold transition-colors disabled:opacity-40 ${
            sign === 1 ? "bg-emerald-600 text-white" : "border border-emerald-500 text-emerald-600 hover:bg-emerald-50"
          }`}
        >
          + 지급
        </button>
        <button
          type="button"
          disabled={DEV_PREVIEW || readOnly}
          onClick={() => openForm(-1)}
          className={`flex-1 rounded-lg py-2 text-[12.5px] font-semibold transition-colors disabled:opacity-40 ${
            sign === -1 ? "bg-red-600 text-white" : "border border-red-400 text-red-600 hover:bg-red-50"
          }`}
        >
          − 회수
        </button>
      </div>
      {readOnly && (
        <div className="mt-1.5 text-[11px] text-amber-600">STG 조회 전용 — 크레딧 조정은 PROD 에서 진행하세요.</div>
      )}

      {sign != null && (
        <div className="mt-2 space-y-1.5">
          <input
            type="number"
            min="1"
            autoFocus
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setConfirming(false); }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="수량"
            className="w-full rounded-md border border-ink-200 px-2 py-1.5 text-sm tabular-nums focus:border-brand-400 focus:outline-none"
          />
          <input
            type="text"
            value={reason}
            onChange={(e) => { setReason(e.target.value); setConfirming(false); }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="사유 (3자 이상, 원장에 기록됨)"
            className="w-full rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
          />
          {expected != null && (
            <div className={`text-[11.5px] tabular-nums ${expected < 0 ? "text-red-500" : "text-ink-400"}`}>
              적용 후 잔액: {fmt.nc(expected)} 크레딧
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className={`w-full rounded-lg py-2 text-[12.5px] font-semibold text-white transition-colors disabled:opacity-50 ${
              confirming ? "bg-amber-500 hover:bg-amber-600" : "bg-ink-900 hover:bg-ink-700"
            }`}
          >
            {submitting ? "처리 중…" : confirming ? "한 번 더 눌러 확인" : sign === 1 ? "지급 적용" : "회수 적용"}
          </button>
        </div>
      )}

      {error && <div className="mt-1.5 text-[12px] text-red-500">{error}</div>}
      {doneMsg && <div className="mt-1.5 text-[12px] font-medium text-emerald-600">{doneMsg}</div>}
    </div>
  );
}

// 사용이력·충전이력 모달(2026-07-27) — 우측 패널(290px)엔 표가 안 들어가서, "내역확인"
// 버튼으로 중앙 모달을 띄우고 전체 사용자 탭과 동일한 UserInlineDetail(도구별/모델별/
// 이용 내역/충전 내역 탭)을 그대로 재사용한다.
function UserHistoryModal({ userId, email, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-[0_24px_64px_-16px_rgba(15,23,42,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-ink-900">{email || "사용자"} — 이용·충전 내역</h2>
            <div className="mt-0.5 truncate font-mono text-[11px] text-ink-400">{userId}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-ink-50/60 px-5 py-4">
          {DEV_PREVIEW ? (
            <div className="py-10 text-center text-[13px] text-ink-400">
              프리뷰 모드에선 실데이터 조회를 하지 않습니다.
            </div>
          ) : (
            <UserInlineDetail userId={userId} />
          )}
        </div>
      </div>
    </div>
  );
}

function DetailPane({ ticket, onStatus, onUpdate, busy, source }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  // STG 조회 상태에서는 모든 mutation 컨트롤(담당자·심각도·처리상태·메모·상태변경·메일)을
  // 일괄 비활성화한다. busy 를 확장해 아래 disabled={busy...} 바인딩 전부에 반영.
  busy = busy || readOnly;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  // ── 이메일 답변 인라인 compose (2026-08-28) — hooks는 early return 전에 선언해야 함 ──
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null); // null | "success" | string(error)

  useEffect(() => { setHistoryOpen(false); setEmailOpen(false); setEmailResult(null); }, [ticket?.id]);
  // internalNote 초안 — 티켓 전환 시 서버 값으로 리셋 (매 keystroke API 호출 금지, 명시적 저장)
  useEffect(() => { setNoteDraft(ticket?.internalNote || ""); }, [ticket?.id, ticket?.internalNote]);

  if (!ticket) {
    return (
      <div className="hidden h-full rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] lg:block" />
    );
  }
  const msgs = ticket.messages || [];
  const fromUser = msgs.filter((m) => m.from === "user").length;
  const fromAdmin = msgs.filter((m) => m.from === "admin").length;
  const initial = (ticket.email || "?").trim().charAt(0).toUpperCase();

  // 자동 이메일 상태(2026-09-28) — 서버 replyNotify 의 최신 항목. 수동 버튼 라벨·배지에 쓴다.
  const notify = latestNotify(ticket.replyNotify);
  const notifyBadge = emailStatusBadge(notify);

  const openEmailCompose = () => {
    setEmailSubject("[AI NOVA] 문의하신 내용에 답변드립니다");
    // 마지막 관리자 답변을 프리필 — 자동 발송 실패 복구 시 다시 타이핑하지 않게.
    const lastAdmin = [...msgs].reverse().find((m) => m.from === "admin")?.text || "";
    setEmailBody(`안녕하세요.\nAI NOVA입니다.\n\n문의주신 내용에 대해 아래와 같이 답변드립니다.\n\n${lastAdmin}\n\n감사합니다.\nAI NOVA 드림`);
    setEmailResult(null);
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (readOnly) { setEmailResult("readonly"); return; }
    if (!emailSubject.trim() || !emailBody.trim() || emailSending) return;
    setEmailSending(true);
    setEmailResult(null);
    try {
      const res = await analyticsApi.adminMailSend({
        emails: [ticket.email],
        subject: emailSubject.trim(),
        bodyText: emailBody.trim(),
      });
      if (res.sentCount > 0) {
        setEmailResult("success");
        // 수동 복구 기록 — 이게 없으면 자동 발송 실패 배지가 영구히 남아 복구 여부를
        // 알 수 없다. 발송 자체(mail_ops)는 건드리지 않고 상태만 티켓에 남긴다.
        // 기존 onUpdate 경로 재사용 → 부모가 갱신까지 처리한다. 기록 실패가 발송 성공 UX 를
        // 깨지 않도록 예외는 삼킨다(발송은 이미 성공했고, 배지만 늦게 갱신될 뿐).
        if (notify?.replyId) {
          try { onUpdate?.({ manualEmailSentFor: notify.replyId }); } catch { /* noop */ }
        }
        setTimeout(() => { setEmailOpen(false); setEmailResult(null); }, 2000);
      } else {
        const errMsg = res.failed?.[0]?.error || "발송에 실패했습니다.";
        setEmailResult(errMsg);
      }
    } catch (err) {
      setEmailResult(err?.message || "이메일 발송에 실패했습니다.");
    }
    setEmailSending(false);
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto rounded-2xl bg-white px-4 py-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col items-center border-b border-ink-100 pb-5">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-400 text-xl font-bold text-white">
          {initial}
        </div>
        <div className="mt-3 max-w-full truncate text-center text-sm font-semibold text-ink-800">
          {ticket.email || "익명"}
        </div>
        {ticket.userId && (
          <div className="mt-0.5 max-w-full truncate font-mono text-[11px] text-ink-400">{ticket.userId}</div>
        )}
      </div>

      <div className="border-b border-ink-100 py-4">
        <div className="mb-1.5 text-xs font-semibold text-ink-500">문의 정보</div>
        <InfoRow label="대화 상태"><StatusBadge status={ticket.status} /></InfoRow>
        <InfoRow label="유형">{ticket.subject && ticket.subject !== "-" ? ticket.subject : "일반"}</InfoRow>
        <InfoRow label="첫 접수">{fmtFull(ticket.createdAt)}</InfoRow>
        <InfoRow label="최근 활동">{fmtFull(ticket.updatedAt || ticket.createdAt)}</InfoRow>
      </div>

      {/* ── CX Phase 1: 내부 운영 (대화 상태 status 와 별개 축) ── */}
      <div className="border-b border-ink-100 py-4">
        <div className="mb-2 text-xs font-semibold text-ink-500">운영 처리</div>

        {/* 담당자 — self/clear 두 액션만 (임의 email 입력 불가) */}
        <div className="mb-3">
          <div className="mb-1 text-[11.5px] text-ink-400">담당자</div>
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[12.5px] font-medium text-ink-700">
              {ticket.assignee || "미배정"}
            </span>
            {ticket.assignee
              ? <button type="button" disabled={busy} onClick={() => onUpdate?.({ assigneeAction: "clear" })}
                  className="shrink-0 rounded-md border border-ink-200 px-2 py-1 text-[11.5px] text-ink-600 hover:bg-ink-50 disabled:opacity-50">할당 해제</button>
              : <button type="button" disabled={busy} onClick={() => onUpdate?.({ assigneeAction: "self" })}
                  className="shrink-0 rounded-md bg-brand-600 px-2 py-1 text-[11.5px] font-medium text-white hover:bg-brand-700 disabled:opacity-50">나에게 할당</button>}
          </div>
        </div>

        {/* Severity — 의견 탭에서는 숨김 (feedback은 severity 불필요) */}
        {source !== "feedback" && (
        <div className="mb-3">
          <div className="mb-1 text-[11.5px] text-ink-400">심각도</div>
          <select value={ticket.severity || "normal"} disabled={busy}
            onChange={(e) => onUpdate?.({ severity: e.target.value })}
            className="w-full rounded-md border border-ink-200 px-2 py-1.5 text-[12.5px] text-ink-700 disabled:opacity-50">
            {SEVERITY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        )}

        {/* processingStatus */}
        <div className="mb-3">
          <div className="mb-1 text-[11.5px] text-ink-400">처리 상태</div>
          <select value={ticket.processingStatus || "new"} disabled={busy}
            onChange={(e) => onUpdate?.({ processingStatus: e.target.value })}
            className="w-full rounded-md border border-ink-200 px-2 py-1.5 text-[12.5px] text-ink-700 disabled:opacity-50">
            {PROC_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>

        {/* resolvedAt (read-only) */}
        {ticket.resolvedAt && (
          <InfoRow label="해결 시각">{fmtFull(ticket.resolvedAt)}</InfoRow>
        )}
      </div>

      {/* ── CX Phase 1: 내부 메모 (사용자 대화와 분리, 명시적 저장) ── */}
      <div className="border-b border-ink-100 py-4">
        <div className="mb-1 text-xs font-semibold text-ink-500">내부 메모</div>
        <p className="mb-2 text-[11px] text-ink-400">내부 운영자만 볼 수 있는 메모입니다.</p>
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={3}
          placeholder="처리 경위·원인·인계 사항 등"
          className="w-full resize-y rounded-md border border-amber-200 bg-amber-50/40 px-2 py-1.5 text-[12.5px] text-ink-800 placeholder:text-ink-300 focus:border-amber-400 focus:outline-none"
        />
        <button
          type="button"
          disabled={busy || noteDraft === (ticket.internalNote || "")}
          onClick={() => onUpdate?.({ internalNote: noteDraft })}
          className="mt-2 w-full rounded-md border border-ink-200 py-1.5 text-[12.5px] font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40"
        >
          메모 저장
        </button>
      </div>

      <div className="border-b border-ink-100 py-4">
        <div className="mb-1.5 text-xs font-semibold text-ink-500">대화 통계</div>
        <InfoRow label="총 메시지">{msgs.length}개</InfoRow>
        <InfoRow label="고객 메시지">{fromUser}개</InfoRow>
        <InfoRow label="관리자 응답">{fromAdmin}개</InfoRow>
      </div>

      {/* userId 없는 티켓(익명/구버전)은 크레딧·이력 조회가 불가능해 섹션 자체를 숨김. */}
      {ticket.userId && (
        <>
          <CreditQuickPanel userId={ticket.userId} />
          <div className="border-b border-ink-100 py-4">
            <div className="mb-2 text-xs font-semibold text-ink-500">사용·충전 이력</div>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="block w-full rounded-lg border border-ink-200 py-2 text-center text-[13px] font-medium text-ink-700 transition-colors hover:bg-ink-50"
            >
              내역확인
            </button>
          </div>
          {historyOpen && (
            <UserHistoryModal userId={ticket.userId} email={ticket.email} onClose={() => setHistoryOpen(false)} />
          )}
        </>
      )}

      <div className="py-4">
        <div className="mb-2 text-xs font-semibold text-ink-500">빠른 작업</div>
        {!emailOpen ? (
          <>
            {notifyBadge && (
              <div className={`mb-1.5 rounded-md px-2 py-1 text-center text-[11.5px] font-medium ${
                notifyBadge.tone === "ok" ? "bg-emerald-50 text-emerald-700"
                : notifyBadge.tone === "error" ? "bg-red-50 text-red-600"
                : "bg-ink-100 text-ink-500"}`}>
                {notifyBadge.text}
              </div>
            )}
            <button
              type="button"
              onClick={openEmailCompose}
              disabled={!ticket.email || ticket.email === "-" || readOnly}
              className="block w-full rounded-lg border border-ink-200 py-2 text-center text-[13px] font-medium text-ink-700 transition-colors hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ✉ {manualEmailLabel(notify)}
            </button>
          </>
        ) : (
          <div className="rounded-lg border border-brand-200 bg-brand-50/30 p-3 space-y-2.5">
            {/* 받는 사람 (read-only) */}
            <div>
              <div className="text-[11px] font-medium text-ink-500 mb-0.5">받는 사람</div>
              <div className="rounded-md border border-ink-200 bg-ink-50 px-2.5 py-1.5 text-[12.5px] text-ink-600">
                {ticket.email}
              </div>
            </div>
            {/* 제목 */}
            <div>
              <div className="text-[11px] font-medium text-ink-500 mb-0.5">제목</div>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-[12.5px] text-ink-700 focus:border-brand-400 focus:outline-none"
              />
            </div>
            {/* 내용 */}
            <div>
              <div className="text-[11px] font-medium text-ink-500 mb-0.5">내용</div>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={6}
                className="w-full resize-y rounded-md border border-ink-200 px-2.5 py-1.5 text-[12.5px] text-ink-700 leading-relaxed focus:border-brand-400 focus:outline-none"
              />
            </div>
            {/* 결과 메시지 */}
            {emailResult === "success" && (
              <div className="rounded-md bg-emerald-50 px-2.5 py-1.5 text-[12px] font-medium text-emerald-700">
                ✓ 이메일을 발송했습니다.
              </div>
            )}
            {emailResult && emailResult !== "success" && (
              <div className="rounded-md bg-red-50 px-2.5 py-1.5 text-[12px] text-red-700">
                발송 실패 — {emailResult}
              </div>
            )}
            {/* 버튼 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setEmailOpen(false); setEmailResult(null); }}
                disabled={emailSending}
                className="flex-1 rounded-lg border border-ink-200 py-2 text-[12.5px] font-medium text-ink-600 hover:bg-ink-50 disabled:opacity-40"
              >
                취소
              </button>
              <button
                type="button"
                onClick={sendEmail}
                disabled={emailSending || !emailSubject.trim() || !emailBody.trim() || readOnly}
                className="flex-1 rounded-lg bg-brand-600 py-2 text-[12.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {emailSending ? "발송 중…" : "이메일 보내기"}
              </button>
            </div>
            <div className="text-[10.5px] text-ink-400">
              발신: AI NOVA &lt;service@ainova.news&gt;
            </div>
          </div>
        )}
        <div className="mt-2 flex gap-2">
          {ticket.status !== "answered" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus("answered")}
              className="flex-1 rounded-lg border border-emerald-500 py-2 text-[12.5px] font-semibold text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
            >
              답변완료
            </button>
          )}
          {ticket.status !== "closed" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus("closed")}
              className="flex-1 rounded-lg border border-ink-200 py-2 text-[12.5px] font-semibold text-ink-500 transition-colors hover:bg-ink-50 disabled:opacity-40"
            >
              문의 닫기
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus("open")}
              className="flex-1 rounded-lg border border-ink-200 py-2 text-[12.5px] font-semibold text-ink-500 transition-colors hover:bg-ink-50 disabled:opacity-40"
            >
              다시 열기
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── VOC(의견) — 읽기 전용 목록(기존 유지, 카드 톤으로 정리) ─────────────────────
function VocPanel() {
  const { data, loading, error } = useAnalyticsQuery((p, s) => analyticsApi.cx.voc({}, s), []);
  return (
    <Panel title="의견 (VOC)">
      {loading ? (
        <Skeleton h={220} />
      ) : error ? (
        <div className="p-5 text-[13.5px] text-ink-500">목록을 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요.</div>
      ) : !data?.items?.length ? (
        <div className="p-5 text-[13.5px] text-ink-500">의견 내역이 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-xs text-ink-500">
                <th className="px-2.5 py-2">사용자</th>
                <th className="px-2.5 py-2">의견</th>
                <th className="px-2.5 py-2">유용 기능</th>
                <th className="px-2.5 py-2">시각</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((v) => (
                <tr key={v.id} className="border-t border-ink-100">
                  <td className="whitespace-nowrap px-2.5 py-2">{v.email || "-"}</td>
                  <td className="px-2.5 py-2">{v.text}</td>
                  <td className="px-2.5 py-2 text-ink-500">{v.usefulFeature || "-"}</td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-ink-500">{fmtRelative(v.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

export default function CxSection() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileCxList />;
  return <DesktopCxSection />;
}

function DesktopCxSection() {
  const [source, setSource] = useState("tickets");
  // 기본 "열림" — 구버전에서 실데이터로 검증된 경로. 선택한 필터를 그대로 서버 state 로
  // 넘겨 조회한다(2026-07-24: state=all 하드코딩이 프로덕션에서 조회 실패를 유발해 되돌림).
  const [filter, setFilter] = useState("open");
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  // 새 문의/추가답장 WS 푸시 — 보고 있는 목록을 즉시 갱신.
  useEffect(() => {
    const onCxEvent = () => setRefreshKey((k) => k + 1);
    window.addEventListener("novadb:cx-event", onCxEvent);
    return () => window.removeEventListener("novadb:cx-event", onCxEvent);
  }, []);

  // 프리뷰 모드에선 목데이터를 로컬 state 로 들고 답장/상태를 직접 반영.
  const [mockTickets, setMockTickets] = useState(() => (DEV_PREVIEW ? buildMockTickets() : null));

  // 선택한 필터를 서버 state 로 그대로 넘긴다(구버전 검증 경로). 프리뷰에선 훅 규칙상
  // 호출은 하되 결과는 목데이터로 대체하고, 목데이터는 클라이언트에서 필터링한다.
  // "feedback" 소스에서는 state=all 로 전체 조회 — subject 기반 클라이언트 필터를 적용하므로
  // 서버 status filter 와 겹치면 데이터가 누락된다.
  const serverState = source === "feedback" ? "all" : filter;
  const { data, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.cx.tickets({ state: serverState }, s),
    [serverState, refreshKey],
  );
  const allTickets = DEV_PREVIEW ? mockTickets || [] : data?.tickets || [];

  // source-based subject filter: "feedback" tab shows only subject==="의견·제안",
  // "tickets" tab shows everything else. Applied before status filter.
  const FEEDBACK_SUBJECT = "의견·제안";
  const sourceFiltered = useMemo(() => {
    if (source === "feedback") return allTickets.filter((t) => t.subject === FEEDBACK_SUBJECT);
    if (source === "tickets") return allTickets.filter((t) => t.subject !== FEEDBACK_SUBJECT);
    return allTickets;
  }, [allTickets, source]);

  const visible = useMemo(() => {
    // "tickets" source: 서버가 status filter를 적용했으므로(state=filter) 그대로 사용.
    // "feedback" source: 서버가 state=all로 가져왔으므로 클라이언트에서 status filter 적용.
    // DEV_PREVIEW: 항상 클라이언트 필터.
    if (source === "feedback") {
      return filter === "all" ? sourceFiltered : sourceFiltered.filter((t) => t.status === filter);
    }
    if (DEV_PREVIEW) {
      return filter === "all" ? sourceFiltered : sourceFiltered.filter((t) => t.status === filter);
    }
    return sourceFiltered; // 서버가 이미 status filter 적용
  }, [sourceFiltered, filter, source]);

  // 선택 유지 — 현재 필터에서 사라지면 목록 첫 항목으로.
  useEffect(() => {
    if (!visible.some((t) => t.id === selectedId)) {
      setSelectedId(visible[0]?.id ?? null);
    }
  }, [visible, selectedId]);

  const selected = allTickets.find((t) => t.id === selectedId) || null;
  // 답변 1건의 멱등 키 — 실패 시 유지(같은 키로 재시도), 성공 시 버림.
  const replyIdRef = useRef(null);

  const sendReply = async (reply, done) => {
    if (!selected) return;
    if (isReadOnlyEnv(useFilterStore.getState().env)) { setActionError("STG 조회 상태에서는 답변 전송이 비활성화됩니다."); return; }
    if (DEV_PREVIEW) {
      const now = new Date().toISOString();
      setMockTickets((prev) => prev.map((t) => t.id === selected.id
        ? { ...t, updatedAt: now, messages: [...t.messages, { from: "admin", text: reply, at: now, by: "미리보기" }] }
        : t));
      done?.();
      return;
    }
    // replyId — 이 답변 1건의 멱등 키. 네트워크 오류로 재시도할 때 같은 값을 재사용해야
    // 서버가 중복 저장·중복 이메일을 막을 수 있다(서버 타임스탬프로는 재시도를 구분 못 함).
    // 실패 시 유지하고, 성공하면 버려서 다음 답변은 새 키를 받는다.
    const replyId = replyIdRef.current || (replyIdRef.current = newReplyId());
    setSending(true);
    setActionError(null);
    try {
      await analyticsApi.cx.ticketUpdate(selected.id, { reply, replyId });
      replyIdRef.current = null;          // 성공 — 다음 답변은 새 키
      done?.();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      // 키를 유지한다 → 사용자가 다시 누르면 같은 replyId 로 안전하게 재시도된다.
      setActionError(err?.message || "전송에 실패했습니다 — 잠시 후 다시 시도해 주세요.");
    }
    setSending(false);
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    if (isReadOnlyEnv(useFilterStore.getState().env)) { setActionError("STG 조회 상태에서는 상태 변경이 비활성화됩니다."); return; }
    if (DEV_PREVIEW) {
      setMockTickets((prev) => prev.map((t) => t.id === selected.id
        ? { ...t, status, updatedAt: new Date().toISOString() } : t));
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await analyticsApi.cx.ticketUpdate(selected.id, { status });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(err?.message || "저장에 실패했습니다 — 잠시 후 다시 시도해 주세요.");
    }
    setBusy(false);
  };

  // CX Phase 1 — 운영 필드 업데이트(severity/processingStatus/assigneeAction/internalNote).
  // status/reply 와 독립. partial update — 전달한 필드만 변경.
  const updateTicket = async (patch) => {
    if (!selected) return;
    if (isReadOnlyEnv(useFilterStore.getState().env)) { setActionError("STG 조회 상태에서는 티켓 변경이 비활성화됩니다."); return; }
    if (DEV_PREVIEW) {
      setMockTickets((prev) => prev.map((t) => t.id === selected.id ? { ...t, ...patch,
        assignee: patch.assigneeAction === "self" ? "미리보기@sedaily.com"
                  : patch.assigneeAction === "clear" ? null : t.assignee,
        updatedAt: new Date().toISOString() } : t));
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await analyticsApi.cx.ticketUpdate(selected.id, patch);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(err?.message || "저장에 실패했습니다 — 잠시 후 다시 시도해 주세요.");
    }
    setBusy(false);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-3 flex items-center justify-end">
        <ToggleGroup options={SOURCE_OPTIONS} value={source} onChange={setSource} />
      </div>

      {source === "voc" ? (
        <VocPanel />
      ) : loading && !allTickets.length ? (
        <Skeleton h={120} />
      ) : error && !allTickets.length ? (
        <div className="rounded-2xl bg-white p-5 text-[13.5px] text-ink-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
          목록을 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요.
        </div>
      ) : (
        <div className="grid h-[calc(100vh-210px)] min-h-[520px] grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)_290px]">
          <TicketList
            tickets={visible}
            filter={filter}
            onFilter={setFilter}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <ConversationPane ticket={selected} onSend={sendReply} sending={sending} error={actionError} />
          <DetailPane ticket={selected} onStatus={changeStatus} onUpdate={updateTicket} busy={busy} source={source} />
        </div>
      )}
    </div>
  );
}
